import { useMemo, useState } from "react";
import { faker } from "@faker-js/faker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trash2, Plus, RefreshCw, Copy, Check, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type FieldType =
  | "firstName" | "lastName" | "fullName" | "email" | "username" | "phone"
  | "uuid" | "id" | "address" | "city" | "country" | "zipcode"
  | "company" | "jobTitle" | "department"
  | "url" | "ipv4" | "userAgent" | "domain"
  | "productName" | "price" | "color" | "boolean"
  | "pastDate" | "futureDate" | "isoDate"
  | "lorem" | "paragraph"
  | "int" | "float";

interface Field {
  id: string;
  name: string;
  type: FieldType;
}

const TYPE_GROUPS: { label: string; types: { value: FieldType; label: string }[] }[] = [
  { label: "Person", types: [
    { value: "firstName", label: "First name" },
    { value: "lastName", label: "Last name" },
    { value: "fullName", label: "Full name" },
    { value: "email", label: "Email" },
    { value: "username", label: "Username" },
    { value: "phone", label: "Phone" },
  ]},
  { label: "Identity", types: [
    { value: "uuid", label: "UUID" },
    { value: "id", label: "Sequential ID" },
  ]},
  { label: "Location", types: [
    { value: "address", label: "Street address" },
    { value: "city", label: "City" },
    { value: "country", label: "Country" },
    { value: "zipcode", label: "ZIP code" },
  ]},
  { label: "Work", types: [
    { value: "company", label: "Company" },
    { value: "jobTitle", label: "Job title" },
    { value: "department", label: "Department" },
  ]},
  { label: "Internet", types: [
    { value: "url", label: "URL" },
    { value: "domain", label: "Domain" },
    { value: "ipv4", label: "IPv4" },
    { value: "userAgent", label: "User agent" },
  ]},
  { label: "Commerce", types: [
    { value: "productName", label: "Product name" },
    { value: "price", label: "Price" },
    { value: "color", label: "Color" },
  ]},
  { label: "Date & numbers", types: [
    { value: "pastDate", label: "Past date" },
    { value: "futureDate", label: "Future date" },
    { value: "isoDate", label: "ISO datetime" },
    { value: "int", label: "Integer" },
    { value: "float", label: "Float" },
    { value: "boolean", label: "Boolean" },
  ]},
  { label: "Text", types: [
    { value: "lorem", label: "Lorem (sentence)" },
    { value: "paragraph", label: "Paragraph" },
  ]},
];

const newId = () => Math.random().toString(36).slice(2, 9);

const DEFAULT_FIELDS: Field[] = [
  { id: newId(), name: "id", type: "uuid" },
  { id: newId(), name: "name", type: "fullName" },
  { id: newId(), name: "email", type: "email" },
  { id: newId(), name: "company", type: "company" },
  { id: newId(), name: "createdAt", type: "isoDate" },
];

const generateValue = (type: FieldType, index: number): unknown => {
  switch (type) {
    case "firstName": return faker.person.firstName();
    case "lastName": return faker.person.lastName();
    case "fullName": return faker.person.fullName();
    case "email": return faker.internet.email().toLowerCase();
    case "username": return faker.internet.username().toLowerCase();
    case "phone": return faker.phone.number();
    case "uuid": return faker.string.uuid();
    case "id": return index + 1;
    case "address": return faker.location.streetAddress();
    case "city": return faker.location.city();
    case "country": return faker.location.country();
    case "zipcode": return faker.location.zipCode();
    case "company": return faker.company.name();
    case "jobTitle": return faker.person.jobTitle();
    case "department": return faker.commerce.department();
    case "url": return faker.internet.url();
    case "domain": return faker.internet.domainName();
    case "ipv4": return faker.internet.ipv4();
    case "userAgent": return faker.internet.userAgent();
    case "productName": return faker.commerce.productName();
    case "price": return Number(faker.commerce.price());
    case "color": return faker.color.human();
    case "pastDate": return faker.date.past().toISOString().slice(0, 10);
    case "futureDate": return faker.date.future().toISOString().slice(0, 10);
    case "isoDate": return faker.date.recent({ days: 30 }).toISOString();
    case "int": return faker.number.int({ min: 1, max: 1000 });
    case "float": return Number(faker.number.float({ min: 0, max: 1000, fractionDigits: 2 }));
    case "boolean": return faker.datatype.boolean();
    case "lorem": return faker.lorem.sentence();
    case "paragraph": return faker.lorem.paragraph();
  }
};

const toCSV = (rows: Record<string, unknown>[]): string => {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
};

const toSQL = (rows: Record<string, unknown>[], table = "test_data"): string => {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const lit = (v: unknown) => {
    if (v === null || v === undefined) return "NULL";
    if (typeof v === "number") return String(v);
    if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
    return `'${String(v).replace(/'/g, "''")}'`;
  };
  return rows
    .map((r) => `INSERT INTO ${table} (${headers.join(", ")}) VALUES (${headers.map((h) => lit(r[h])).join(", ")});`)
    .join("\n");
};

const TestDataGenerator = () => {
  const { toast } = useToast();
  const [fields, setFields] = useState<Field[]>(DEFAULT_FIELDS);
  const [count, setCount] = useState(25);
  const [tableName, setTableName] = useState("users");
  const [seed, setSeed] = useState(0);
  const [copied, setCopied] = useState<string>("");

  const data = useMemo(() => {
    if (seed) faker.seed(seed);
    const safeCount = Math.max(1, Math.min(1000, Number(count) || 1));
    const rows: Record<string, unknown>[] = [];
    for (let i = 0; i < safeCount; i++) {
      const row: Record<string, unknown> = {};
      for (const f of fields) {
        const key = f.name.trim() || f.type;
        row[key] = generateValue(f.type, i);
      }
      rows.push(row);
    }
    return rows;
  }, [fields, count, seed]);

  const json = useMemo(() => JSON.stringify(data, null, 2), [data]);
  const csv = useMemo(() => toCSV(data), [data]);
  const sql = useMemo(() => toSQL(data, tableName.trim() || "test_data"), [data, tableName]);

  const updateField = (id: string, patch: Partial<Field>) =>
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  const removeField = (id: string) =>
    setFields((prev) => (prev.length > 1 ? prev.filter((f) => f.id !== id) : prev));

  const addField = () =>
    setFields((prev) => [...prev, { id: newId(), name: `field_${prev.length + 1}`, type: "lorem" }]);

  const copy = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
    toast({ title: "Copied to clipboard" });
  };

  const download = (text: string, filename: string, mime: string) => {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card p-4 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label htmlFor="td-count">Rows</Label>
            <Input id="td-count" type="number" min={1} max={1000} value={count} onChange={(e) => setCount(Number(e.target.value))} className="w-28" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="td-table">SQL table name</Label>
            <Input id="td-table" value={tableName} onChange={(e) => setTableName(e.target.value)} className="w-44" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="td-seed">Seed (optional)</Label>
            <Input id="td-seed" type="number" value={seed || ""} onChange={(e) => setSeed(Number(e.target.value) || 0)} placeholder="e.g. 42" className="w-32" />
          </div>
          <Button variant="outline" onClick={() => setSeed((s) => (s ? s + 1 : Math.floor(Math.random() * 100000)))}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Regenerate
          </Button>
        </div>

        <div className="space-y-2">
          <Label>Schema</Label>
          <div className="space-y-2">
            {fields.map((f) => (
              <div key={f.id} className="flex flex-wrap items-center gap-2">
                <Input
                  value={f.name}
                  onChange={(e) => updateField(f.id, { name: e.target.value })}
                  placeholder="field name"
                  className="w-44"
                />
                <Select value={f.type} onValueChange={(v) => updateField(f.id, { type: v as FieldType })}>
                  <SelectTrigger className="w-56">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_GROUPS.map((g) => (
                      <div key={g.label}>
                        <div className="px-2 py-1 text-xs text-muted-foreground">{g.label}</div>
                        {g.types.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" onClick={() => removeField(f.id)} aria-label="Remove field">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={addField}>
            <Plus className="h-4 w-4 mr-2" />
            Add field
          </Button>
        </div>
      </div>

      <Tabs defaultValue="json">
        <TabsList>
          <TabsTrigger value="json">JSON</TabsTrigger>
          <TabsTrigger value="csv">CSV</TabsTrigger>
          <TabsTrigger value="sql">SQL</TabsTrigger>
        </TabsList>
        {([
          { v: "json", text: json, file: "test-data.json", mime: "application/json" },
          { v: "csv", text: csv, file: "test-data.csv", mime: "text/csv" },
          { v: "sql", text: sql, file: "test-data.sql", mime: "text/plain" },
        ] as const).map((t) => (
          <TabsContent key={t.v} value={t.v}>
            <div className="rounded-xl border border-border bg-card">
              <div className="flex items-center justify-between p-3 border-b border-border">
                <span className="text-sm text-muted-foreground">{data.length} rows</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => copy(t.text, t.v)}>
                    {copied === t.v ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                    Copy
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => download(t.text, t.file, t.mime)}>
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
              <pre className="p-4 text-xs overflow-auto max-h-[480px] font-mono">{t.text}</pre>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default TestDataGenerator;